import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { FiTrash2, FiShoppingBag } from 'react-icons/fi';

const Cart = () => {
  const navigate = useNavigate();
  const { cart, removeFromCart, updateQuantity, cartTotal, clearCart } = useCart();

  if (cart.length === 0) {
    return (
      <div className="py-24 lg:py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 text-center">
          <FiShoppingBag className="w-16 h-16 text-text-muted mx-auto mb-6" />
          <h2 className="text-3xl font-heading font-bold text-primary-main mb-4" data-testid="empty-cart-message">
            Your cart is empty
          </h2>
          <p className="text-text-secondary mb-8">Add some research peptides to get started</p>
          <button
            onClick={() => navigate('/shop')}
            className="btn-primary"
            data-testid="continue-shopping-button"
          >
            Continue Shopping
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="py-24 lg:py-32">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <h1 className="text-4xl sm:text-5xl font-heading font-bold tracking-tighter text-primary-main mb-12" data-testid="cart-heading">
          Shopping Cart
        </h1>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
          {/* Cart Items */}
          <div className="lg:col-span-2 space-y-4">
            {cart.map((item) => (
              <div
                key={item.id}
                className="bg-white border border-slate-200 rounded-lg p-6 flex gap-6"
                data-testid={`cart-item-${item.sku}`}
              >
                <div className="w-24 h-24 bg-background-subtle rounded-lg flex-shrink-0 overflow-hidden">
                  <img
                    src={item.product_image || "https://images.unsplash.com/photo-1589807502991-d64e6459096c?crop=entropy&cs=srgb&fm=jpg&q=85"}
                    alt={item.name}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="flex-1">
                  <h3 className="font-heading font-semibold text-lg text-primary-main mb-1" data-testid={`cart-item-name-${item.sku}`}>
                    {item.name}
                  </h3>
                  <p className="text-sm text-text-muted font-mono mb-3">{item.concentration}</p>
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => updateQuantity(item.id, item.quantity - 1)}
                        className="w-8 h-8 border border-slate-200 rounded hover:bg-slate-50 transition-colors"
                        data-testid={`decrease-quantity-${item.sku}`}
                      >
                        -
                      </button>
                      <span className="w-12 text-center font-mono" data-testid={`quantity-${item.sku}`}>{item.quantity}</span>
                      <button
                        onClick={() => updateQuantity(item.id, item.quantity + 1)}
                        className="w-8 h-8 border border-slate-200 rounded hover:bg-slate-50 transition-colors"
                        data-testid={`increase-quantity-${item.sku}`}
                      >
                        +
                      </button>
                    </div>
                    <button
                      onClick={() => removeFromCart(item.id)}
                      className="text-error hover:text-red-700 transition-colors"
                      data-testid={`remove-item-${item.sku}`}
                    >
                      <FiTrash2 className="w-5 h-5" />
                    </button>
                  </div>
                </div>
                <div className="text-right">
                  <p className="font-mono text-lg font-semibold text-primary-main" data-testid={`item-total-${item.sku}`}>
                    ${(item.price * item.quantity).toFixed(2)} AUD
                  </p>
                  <p className="text-sm text-text-muted font-mono">${item.price} each</p>
                </div>
              </div>
            ))}
          </div>

          {/* Order Summary */}
          <div>
            <div className="bg-background-subtle border border-slate-200 rounded-lg p-6 sticky top-24">
              <h3 className="text-xl font-heading font-bold text-primary-main mb-6">Order Summary</h3>
              <div className="space-y-3 mb-6">
                <div className="flex justify-between text-text-secondary">
                  <span>Subtotal</span>
                  <span className="font-mono" data-testid="cart-subtotal">${cartTotal.toFixed(2)} AUD</span>
                </div>
                <div className="flex justify-between text-text-secondary">
                  <span>Shipping</span>
                  <span className="font-mono">Calculated at checkout</span>
                </div>
                <div className="border-t border-slate-200 pt-3 flex justify-between text-lg font-semibold">
                  <span className="font-heading">Total</span>
                  <span className="font-mono text-primary-main" data-testid="cart-total">${cartTotal.toFixed(2)} AUD</span>
                </div>
              </div>
              <button
                onClick={() => navigate('/checkout')}
                className="btn-primary w-full mb-3"
                data-testid="proceed-checkout-button"
              >
                Proceed to Checkout
              </button>
              <button
                onClick={() => navigate('/shop')}
                className="btn-secondary w-full"
                data-testid="continue-shopping-button"
              >
                Continue Shopping
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Cart;
